import { Directive, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { ExceptionEvent } from '../exception';
import { ModalDismissGuard } from '../modal/modal-dismiss.guard';
import { ModalBusyIndicatorGuard } from '../modal/modalbusyindicator.guard';
import { RoutingService } from '../routing/routing.service';
import { ToastService } from '../toast';
import { LogicalUnitBaseComponent } from './logicalunit-base.component';
import { LogicalUnitModalBaseService } from './logicalunit-modal-base.service';
import { IOpenModalLogicalUnitParams } from './logicalunit-modal.interface';

/**
 * Base logical unit component class
 *
 * @export
 * @abstract
 * @class LogicalUnitBaseComponent
 */
@Directive()
export abstract class LogicalUnitModalBaseComponent<T extends LogicalUnitModalBaseService, U> extends LogicalUnitBaseComponent<T> implements OnDestroy, OnInit {
    /**
     * Method used to handle non business errors.
     *
     * @memberof LogicalUnitBaseComponent
     * @example
     *      // Handle javascript errors locally.
     *      this.handleError = (event: ExceptionEvent) => {
     *          if (anyCondition) {
     *              // Do not execute the framework behavior.
     *              event.preventDefault();
     *          }
     *      };
     */
    public override handleError: (error: ExceptionEvent) => void;

    /**
     * Method used to modify the url sent to google analytics.
     *
     *
     * @memberof LogicalUnitBaseComponent
     */
    public override gaPageMap: (url: string) => string;

    /**
     * Hook method called when the logical unit is activated.
     *
     * @memberof LogicalUnitBaseService
     */
    public activated: () => void;

    protected override routingService: RoutingService;

    /**
     * The value passed back to the caller's callback when the modal closes (assign it before
     * calling `dialog.hide()`, e.g. `this.result = x; this.dialog.hide();`).
     *
     * @memberof LogicalUnitModalBaseComponent
     */
    result: U;

    /**
     * Creates an instance of LogicalUnitModalBaseComponent.
     * @param {T} service
     * @param {BsModalRef} dialog
     * @param {ToastService} toastService
     * @param {RoutingService} routingService
     * @param {ModalDismissGuard} modalDismissGuard
     * @memberof LogicalUnitModalBaseComponent
     */
    constructor(
        public override service: T,
        dialog: BsModalRef,
        toastService: ToastService,
        routingService: RoutingService,
        modalDismissGuard: ModalDismissGuard,
    ) {
        super(service, toastService, routingService);

        const openingParams = service.openingParams as IOpenModalLogicalUnitParams<any>;

        if (Array.isNullOrEmpty(openingParams.canDeactivate)) {
            openingParams.canDeactivate = [];
        }
        if (openingParams.canDeactivate.indexOf(ModalBusyIndicatorGuard) === -1) {
            openingParams.canDeactivate.push(ModalBusyIndicatorGuard);
        }

        // The closeInterceptor calling this guard is wired centrally by U2000_ModalProvider
        // (before the modal is shown, using this same ModalDismissGuard singleton) — setting the
        // opening params here (after) is enough since it is only read once a dismiss is attempted.
        modalDismissGuard.setOpeningParams(openingParams);
    }

    override ngOnInit() {
        super.ngOnInit();
        this.routingService.openModalLogicalUnit(this.service.logicalUnitId);
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.routingService.closeModalLogicalUnit(this.service.logicalUnitId);
    }
}
